import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Phone, ArrowRight, Menu, X } from "lucide-react";
import logo from "@/assets/crs-logo.webp";
import { BookACallButton } from "@/components/book-a-call";
import { ensureGhlPreconnect } from "@/lib/ghl-preconnect";
import { SITE } from "@/lib/site";
import { MobileStickyCtaBar, ReviewsBar } from "@/components/site-header";

function scrollToId(id: string) {
  if (typeof window === "undefined") return;
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function scrollToHeroForm() {
  scrollToId("damp-survey-hero-form");
}

const NAV_LINKS: { label: string; id: string }[] = [
  { label: "Services", id: "methodology" },
  { label: "Pricing", id: "pricing" },
  { label: "Contact", id: "contact" },
];

export function DampSurveyHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    ensureGhlPreconnect();
  }, []);

  return (
    <header className="site-sticky-chrome bg-background/95 backdrop-blur border-b border-border">
      <div className="container-page flex items-center justify-between gap-3 py-3 md:py-4">
        <Link to="/" className="flex items-center shrink-0" aria-label="CRS Energy home">
          <img
            src={logo}
            alt="CRS Energy"
            width={900}
            height={345}
            className="h-10 md:h-12 w-auto"
            decoding="async"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-6" aria-label="Primary">
          <Link
            to="/"
            className="text-sm font-semibold text-foreground hover:text-accent transition-colors"
          >
            Home
          </Link>
          {NAV_LINKS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => scrollToId(item.id)}
              className="text-sm font-semibold text-foreground hover:text-accent transition-colors"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={SITE.phoneTel}
            className="hidden lg:inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-foreground hover:text-accent transition-colors"
          >
            <Phone className="h-4 w-4" aria-hidden />
            {SITE.phone}
          </a>
          <BookACallButton
            className="hidden md:inline-flex px-3 py-2 text-xs md:text-sm"
            label="Book a Call"
          />
          <button
            type="button"
            onClick={scrollToHeroForm}
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2.5 md:px-5 md:py-2.5 rounded-full bg-accent text-accent-foreground text-xs md:text-sm font-bold hover:bg-accent/90 transition-colors shadow-cta"
          >
            Book A Survey
            <ArrowRight className="h-3.5 w-3.5 md:h-4 md:w-4" />
          </button>
          <button
            type="button"
            className="md:hidden p-3 rounded-md text-foreground hover:bg-secondary min-h-11 min-w-11 inline-flex items-center justify-center"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="md:hidden border-t border-border bg-background" aria-label="Mobile">
          <div className="container-page py-3 flex flex-col gap-1">
            <Link
              to="/"
              className="px-3 py-3 text-sm font-semibold rounded-lg hover:bg-secondary"
              onClick={() => setMobileOpen(false)}
            >
              Home
            </Link>
            {NAV_LINKS.map((item) => (
              <button
                key={item.id}
                type="button"
                className="px-3 py-3 text-sm font-semibold text-left rounded-lg hover:bg-secondary"
                onClick={() => {
                  setMobileOpen(false);
                  scrollToId(item.id);
                }}
              >
                {item.label}
              </button>
            ))}
            <button
              type="button"
              className="mt-2 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full bg-accent text-accent-foreground text-sm font-bold"
              onClick={() => {
                setMobileOpen(false);
                scrollToHeroForm();
              }}
            >
              Book A Survey
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </nav>
      )}

      <MobileStickyCtaBar />
      <ReviewsBar />
    </header>
  );
}
